import { Link } from 'react-router-dom';
import { FileText, Users, HelpCircle, Phone, Info, Shield, Lock } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import BhumiLogo from './BhumiLogo';

const Footer = ({ isDark: propIsDark }) => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSite();
  const { t } = useLanguage();
  const { isDark: themeIsDark } = useTheme();
  
  // Use prop if provided, otherwise use theme context
  const isDark = propIsDark !== undefined ? propIsDark : themeIsDark;

  const footerLinks = [
    {
      title: t('footer.info'),
      links: [
        { path: '/guides', label: t('nav.guides'), icon: FileText },
        { path: '/faq', label: t('nav.faq'), icon: HelpCircle },
      ],
    },
    {
      title: t('footer.service'),
      links: [
        { path: '/services', label: t('nav.services'), icon: Users },
        { path: '/contact', label: t('nav.contact'), icon: Phone },
      ],
    },
    {
      title: t('footer.organization'),
      links: [
        { path: '/about', label: t('nav.about'), icon: Info },
        { path: '/privacy', label: t('privacy.privacyPolicy'), icon: Lock },
      ],
    },
  ];

  return (
    <footer className={`${isDark ? 'bg-[#0A0A0A] text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Disclaimer */}
      <div className={`border-t ${isDark ? 'border-white/10 bg-[#0D330F]/30' : 'border-gray-200 bg-[#E8F5E9]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-start gap-3">
            <Shield className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-[#FBC02D]' : 'text-[#2E7D32]'}`} />
            <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-testid="disclaimer">
              <strong>{t('footer.important')}:</strong> {settings.site_name} {t('footer.disclaimer')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <BhumiLogo size="sm" showText={false} isDark={isDark} />
              <span className="text-xl md:text-2xl font-bold">{settings.site_name}</span>
            </Link>
            <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {settings.site_tagline}
            </p>
          </div>

          {/* Links */}
          {footerLinks.map((section) => (
            <div key={section.title}>
              <h4 className={`font-semibold mb-3 md:mb-4 text-sm md:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {section.title}
              </h4>
              <ul className="space-y-2 md:space-y-3">
                {section.links.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className={`flex items-center gap-2 text-xs sm:text-sm transition-colors ${
                        isDark
                          ? 'text-gray-400 hover:text-[#FBC02D]'
                          : 'text-gray-600 hover:text-[#2E7D32]'
                      }`}
                    >
                      <link.icon size={14} className="flex-shrink-0" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className={`mt-8 md:mt-12 pt-6 md:pt-8 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              © {currentYear} {settings.site_name}। {t('footer.rights')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
